import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Archive,
  ContentCopy,
  Edit,
  Insights,
  PublishedWithChanges,
  Unpublished,
  Unarchive,
} from '@mui/icons-material';
import NavHistoryTracker from '../../../navigation/NavHistoryTracker';
import AdminConfirmDialog from '../Common/AdminConfirmDialog';
import { CacheKey } from '../../../types/cache-keys';
import {
  cloneAssessment,
  useAssessmentMutations,
  useFetchAssessments,
} from '../../../hooks/assessment-hooks';
import { Assessment, purposeLabel, uniqueKey } from '../../../support/assessments';
import AssessmentBuilder from './AssessmentBuilder';
import AssessmentResponsesReview from './AssessmentResponsesReview';

/**
 * Management → Assessments: the assessment builder's home.
 *
 * A two-mode page. The list is every assessment (drafts and published; archived hidden unless
 * asked for), with the lifecycle actions on each — clone, publish/unpublish, archive/restore — and
 * a single "Edit" that swaps the list for the builder. All editing (the assessment's own fields and
 * its section/question tree) happens inline in that builder; there is no metadata modal. Behind
 * `page.assessmentAdmin.view`; the generic API re-checks every write against
 * `api.assessment*.{create,update,delete}`, so this page assumes it may write.
 *
 * Delete is a soft archive (`is_active = false`), not a row delete: an assessment may already have
 * response sets recorded against it, and losing those to a stray click is not recoverable. A hard
 * delete is still possible through the generic API for a truly empty draft, but is not offered here.
 */

const AdminAssessments: React.FC = () => {
  const queryClient = useQueryClient();
  const { create, update } = useAssessmentMutations('assessment');

  const { data: assessments = [], isLoading } = useFetchAssessments({});

  const [builderId, setBuilderId] = useState<number | null>(null);
  const [reviewId, setReviewId] = useState<number | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [pendingArchive, setPendingArchive] = useState<Assessment | null>(null);
  const [cloningId, setCloningId] = useState<number | null>(null);

  const rows = useMemo(() => {
    const list = (assessments as Assessment[]).filter((a) =>
      showArchived ? true : a.is_active !== false
    );
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [assessments, showArchived]);

  // No create modal: a new assessment is created as an unpublished draft with an auto key and then
  // opened straight in the builder, where its name and every other field are edited inline.
  const handleCreate = async () => {
    const created: Assessment = await create.mutateAsync({
      key: uniqueKey('assessment', (assessments as Assessment[]).map((a) => a.key)),
      name: 'New Assessment',
      description: '',
      purpose: 'general',
      subject_type: null,
      is_published: false,
      allow_anonymous: false,
      is_active: true,
      metadata: {},
    });
    await queryClient.invalidateQueries({ queryKey: [String(CacheKey.Assessments)] });
    setBuilderId(created.id);
  };

  const togglePublished = (assessment: Assessment) =>
    update.mutate({ ...assessment, is_published: !assessment.is_published });

  const toggleArchived = async () => {
    if (!pendingArchive) return;
    await update.mutateAsync({ ...pendingArchive, is_active: pendingArchive.is_active === false });
    setPendingArchive(null);
  };

  const restore = (assessment: Assessment) =>
    update.mutate({ ...assessment, is_active: true });

  const handleClone = async (assessment: Assessment) => {
    setCloningId(assessment.id);
    try {
      const created = await cloneAssessment(assessment);
      await queryClient.invalidateQueries({ queryKey: [String(CacheKey.Assessments)] });
      setBuilderId(created.id);
    } finally {
      setCloningId(null);
    }
  };

  if (builderId != null) {
    return (
      <Box p={2}>
        <NavHistoryTracker />
        <AssessmentBuilder assessmentId={builderId} onBack={() => setBuilderId(null)} />
      </Box>
    );
  }

  if (reviewId != null) {
    return (
      <Box p={2}>
        <NavHistoryTracker />
        <AssessmentResponsesReview assessmentId={reviewId} onBack={() => setReviewId(null)} />
      </Box>
    );
  }

  return (
    <Box p={2}>
      <NavHistoryTracker />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={2}>
        <Box>
          <Typography variant="h5">Assessments</Typography>
          <Typography variant="body2" color="text.secondary">
            Build and manage reusable question sets — workshop pre-work, feedback forms and surveys.
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
            }
            label="Show archived"
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            disabled={create.isPending}
          >
            New Assessment
          </Button>
        </Box>
      </Box>

      <Paper variant="outlined">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell width={160}>Purpose</TableCell>
                <TableCell width={120}>Status</TableCell>
                <TableCell width={220}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress size={22} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      No assessments yet. Create one to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((assessment) => {
                  const archived = assessment.is_active === false;
                  return (
                    <TableRow key={assessment.id} hover>
                      <TableCell>
                        <Typography variant="body2">{assessment.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {assessment.key}
                        </Typography>
                      </TableCell>
                      <TableCell>{purposeLabel(assessment.purpose)}</TableCell>
                      <TableCell>
                        {archived ? (
                          <Chip size="small" label="Archived" />
                        ) : (
                          <Chip
                            size="small"
                            color={assessment.is_published ? 'success' : 'default'}
                            label={assessment.is_published ? 'Published' : 'Draft'}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Edit — fields, sections & questions">
                          <IconButton size="small" onClick={() => setBuilderId(assessment.id)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Responses">
                          <IconButton size="small" onClick={() => setReviewId(assessment.id)}>
                            <Insights fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Clone">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleClone(assessment)}
                              disabled={cloningId === assessment.id}
                            >
                              {cloningId === assessment.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <ContentCopy fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {!archived && (
                          <Tooltip title={assessment.is_published ? 'Unpublish' : 'Publish'}>
                            <IconButton size="small" onClick={() => togglePublished(assessment)}>
                              {assessment.is_published ? (
                                <Unpublished fontSize="small" />
                              ) : (
                                <PublishedWithChanges fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {archived ? (
                          <Tooltip title="Restore">
                            <IconButton size="small" onClick={() => restore(assessment)}>
                              <Unarchive fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Archive">
                            <IconButton size="small" onClick={() => setPendingArchive(assessment)}>
                              <Archive fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <AdminConfirmDialog
        open={!!pendingArchive}
        title="Archive Assessment"
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              {`Archive "${pendingArchive?.name || 'this assessment'}"?`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              It will be hidden and can no longer be filled in. Responses already collected are kept,
              and you can restore it later.
            </Typography>
          </Box>
        }
        confirmLabel="Archive"
        cancelLabel="Cancel"
        confirmColor="warning"
        loading={update.isPending}
        onConfirm={toggleArchived}
        onCancel={() => setPendingArchive(null)}
      />
    </Box>
  );
};

export default AdminAssessments;
